import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { IncoTerms } from '@invenco/sales-interface/interfaces';
import type { ShippingMethodDTO } from '@invenco/shipping-interface';
import type { WarehouseCarrierServiceDTO } from '@invenco/warehousing-interface';

import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

export const DEFAULT_CARRIER_ACCOUNT_ID = 'default';
export const DEFAULT_SHIPPING_METHOD_ID = 'default';

export const warehouseCarrierServiceFormSchema = z
  .object({
    wmsInstanceId: z.string().min(1, 'Select a WMS instance'),
    carrierId: z.string().min(1, 'Select a carrier'),
    carrierServiceId: z.string().min(1, 'Select a carrier service'),
    carrierAccountId: z.string(),
    shippingMethodId: z.string(),
    signatureRequired: z.enum(['true', 'false']),
    incoTerms: z.enum(IncoTerms),
    primaryCode: z.string().min(1, 'Enter a primary export code'),
    secondaryCodes: z.array(z.string()),
  })
  .refine(({ primaryCode, secondaryCodes }) => !secondaryCodes.includes(primaryCode), {
    path: ['secondaryCodes'],
    message: 'Primary and secondary export codes must be unique',
  })
  .refine(({ secondaryCodes }) => new Set(secondaryCodes).size === secondaryCodes.length, {
    path: ['secondaryCodes'],
    message: 'Secondary export codes must be unique',
  });

export type WarehouseCarrierServiceFormValues = z.infer<typeof warehouseCarrierServiceFormSchema>;

export function getFormValues(
  warehouseCarrierService?: WarehouseCarrierServiceDTO,
  carrierAccountId: string = DEFAULT_CARRIER_ACCOUNT_ID,
): WarehouseCarrierServiceFormValues {
  return {
    wmsInstanceId: warehouseCarrierService?.wmsInstanceId ?? '',
    carrierId: warehouseCarrierService?.carrierId ?? '',
    carrierServiceId: warehouseCarrierService?.carrierServiceId ?? '',
    carrierAccountId,
    shippingMethodId: warehouseCarrierService?.shippingMethodId ?? DEFAULT_SHIPPING_METHOD_ID,
    signatureRequired: warehouseCarrierService?.signatureRequired === false ? 'false' : 'true',
    incoTerms: warehouseCarrierService?.incoTerms ?? IncoTerms.DDU,
    primaryCode: warehouseCarrierService?.codes?.find(({ isPrimary }) => isPrimary)?.code ?? '',
    secondaryCodes:
      warehouseCarrierService?.codes
        ?.filter(({ isPrimary }) => !isPrimary)
        .map(({ code }) => code) ?? [],
  };
}

export function renderShippingMethodName(shippingMethod: ShippingMethodDTO): string {
  return `${shippingMethod.name || ''}_${
    shippingMethod.type === 'WAREHOUSE'
      ? shippingMethod.locationName
      : shippingMethod.warehouseLocationId
  }_${shippingMethod.accountName || ''}_${shippingMethod.bookingCarrierAccountName || ''}`;
}

type UseWarehouseCarrierServiceFormProps = {
  defaultValues: WarehouseCarrierServiceFormValues;
  /**
   * Drives the carrier accounts query before the form holds a carrier, so an
   * account can be resolved from saved data ahead of form hydration.
   */
  fallbackCarrierId?: string;
  enabled?: boolean;
};

export function useWarehouseCarrierServiceForm({
  defaultValues,
  fallbackCarrierId,
  enabled = true,
}: UseWarehouseCarrierServiceFormProps) {
  const { warehousingGateway, shippingGateway } = useGateways();

  const form = useForm<WarehouseCarrierServiceFormValues>({
    resolver: zodResolver(warehouseCarrierServiceFormSchema),
    defaultValues,
  });

  const selectedCarrierId = form.watch('carrierId');
  const selectedCarrierServiceId = form.watch('carrierServiceId');
  const selectedCarrierAccountId = form.watch('carrierAccountId');

  useEffect(
    () =>
      form.subscribe({
        name: ['carrierId', 'carrierServiceId', 'carrierAccountId'],
        callback: ({ name }) => {
          if (name === 'carrierId') {
            form.setValue('carrierServiceId', '');
            form.setValue('carrierAccountId', DEFAULT_CARRIER_ACCOUNT_ID);
            form.setValue('shippingMethodId', DEFAULT_SHIPPING_METHOD_ID);
          }

          if (name === 'carrierServiceId') {
            form.setValue('carrierAccountId', DEFAULT_CARRIER_ACCOUNT_ID);
            form.setValue('shippingMethodId', DEFAULT_SHIPPING_METHOD_ID);
          }

          if (name === 'carrierAccountId') {
            form.setValue('shippingMethodId', DEFAULT_SHIPPING_METHOD_ID);
          }
        },
      }),
    [form],
  );

  const { data: carriers = [], isLoading: isLoadingCarriers } = useQuery({
    queryKey: ['carriers', { include: { carrierServices: true } }],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarriers(
          { include: { carrierServices: true }, cursor, take },
          { signal },
        ),
      ),
    enabled,
  });

  const { data: wmsInstances, isLoading: isLoadingWmsInstances } = useQuery({
    queryKey: ['wmsInstances'],
    queryFn: ({ signal }) => warehousingGateway.getWmsInstances(undefined, { signal }),
    enabled,
  });

  const accountsCarrierId = selectedCarrierId || fallbackCarrierId;
  const { data: carrierAccounts, isLoading: isLoadingCarrierAccounts } = useQueryWithInput({
    parentKey: 'carrierAccounts',
    input: { carrierIds: accountsCarrierId ? [accountsCarrierId] : [] },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierAccounts({ ...input, cursor, take }, { signal }),
      ),
    enabled: enabled && !!accountsCarrierId,
  });

  const { data: shippingMethods, isLoading: isLoadingShippingMethods } = useQueryWithInput({
    parentKey: 'shippingMethods',
    input: {
      carrierServiceId: selectedCarrierServiceId,
      carrierAccountId: selectedCarrierAccountId,
    },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getShippingMethods({ ...input, cursor, take }, { signal }),
      ),
    enabled:
      enabled &&
      !!selectedCarrierServiceId &&
      !!selectedCarrierAccountId &&
      selectedCarrierAccountId !== DEFAULT_CARRIER_ACCOUNT_ID,
  });

  const selectedCarrier = carriers.find((carrier) => carrier.id === selectedCarrierId);
  const selectedCarrierService = selectedCarrier?.carrierServices?.find(
    (carrierService) => carrierService.id === selectedCarrierServiceId,
  );
  const filteredShippingMethods = useMemo(() => {
    const warehouseShippingMethods = shippingMethods?.filter(
      (shippingMethod) => shippingMethod.type === 'WAREHOUSE',
    );

    return warehouseShippingMethods && warehouseShippingMethods.length > 0
      ? warehouseShippingMethods
      : shippingMethods;
  }, [shippingMethods]);

  return {
    form,
    carriers,
    isLoadingCarriers,
    wmsInstances,
    isLoadingWmsInstances,
    carrierAccounts,
    isLoadingCarrierAccounts,
    filteredShippingMethods,
    isLoadingShippingMethods,
    selectedCarrier,
    selectedCarrierService,
    selectedCarrierId,
    selectedCarrierServiceId,
    selectedCarrierAccountId,
  };
}
